<template>
    <div class="dod-bubbles">

        <svg class="dod-bubble fade-in" :data-movement-factor="bubble.movement" :class="bubble.variant" :id="bubble.id"
            :style="bubbleStyle(bubble)" v-for="bubble in _bubbles" :key="bubble.id" viewBox="0 0 100 100">
            <circle v-if="bubble.shape == 'circle'" cx="50" cy="50" r="50"></circle>
            <rect v-else-if="bubble.shape == 'rect'" x="5" y="5" width="50" height="50" rx="10" ry="10"
                ></rect>
            <polygon v-else-if="bubble.shape == 'triangle'" points="50,0 100,100 0,100">
            </polygon>
            <polygon v-else-if="bubble.shape == 'polygon'" :points="bubble.points" >
            </polygon>
            <polygon v-else-if="bubble.shape == 'star'" points="79,91 50,70 21,91 33,57 2,35 39,35 50,1 61,35 98,35 67,57" />
            <polygon v-else-if="bubble.shape == 'pentagon'" points="50,10 90,40 75,90 25,90 10,40" />
            <path v-else-if="bubble.shape == 'heart'"   d="M 50.263157,99.210526 Q -13.834587,54.790605 4.4790548,17.774005 22.792696,-11.839276 50.263157,10.370685 77.73362,-11.839276 96.047261,17.774005 114.3609,54.790605 50.263157,99.210526"  />
        </svg>
    </div>
</template>

<script setup>
import { ref, defineProps, computed, onMounted, getCurrentInstance } from 'vue';
import dvariants from "@/dod/enums/dvariants";
import { uuid as uuid } from 'vue3-uuid'; // Import the uuid function

// globals
const { appContext } = getCurrentInstance()
const drandom = appContext.config.globalProperties.$drandom;
const dfunctions = appContext.config.globalProperties.$dfunctions;

const pattern_bounce = 'bouncing-box';
const pattern_random = "random";
const base_shapes = 5;

// props
const props = defineProps({
    pattern: { type: String, default: null },
    shapes: { type: Array, default: () => ['circle', 'star', 'pentagon', 'triangle'] },
    quantity: { type: Number, default: null },
    min_size: { type: Number, default: 10 },
    max_size: { type: Number, default: 600 },
    default_center: { type: Boolean, default: false}
})

// data
const _bubbles = ref(null);


// computed
const nr_shapes = computed(() => {
    if(props.quantity)
        return props.quantity;
    // else aut based on screen size
    const shapesFactor = Math.floor(dfunctions.bodyWidth() / 200);
    return base_shapes + shapesFactor
})
const _min_size = computed(() => props.min_size);
const _max_size = computed(() => 
{
    if(props.max_size > dfunctions.bodyWidth())
        return dfunctions.bodyWidth() * 0.7;
    return props.max_size;
})

// functions

function bubbleStyle(bubble) {
    var style = `width:${bubble.size}px;height:${bubble.size}px;opacity:${bubble.opacity};fill:var(--bs-${bubble.variant});top:${bubble.y}px;left:${bubble.x};`;
    return style;
}

function random(min, max) {
    return drandom.randomInt(min, max);
}

function getRandomPolygonPoints(numPoints) {
    const points = [];

    for (let i = 0; i < numPoints; i++) {
        // Generate random x and y coordinates within the range [0, 100]
        const x = Math.random() * 100;
        const y = Math.random() * 100;
        points.push(`${x},${y}`);
    }

    return points.join(' ');
}

function GenerateBubbles() {
    _bubbles.value = [];
    for (var i = 0; i < nr_shapes.value; i++) {
        var variant_index = random(0, dvariants.bubble_variants.length - 1);
        var angle = Math.random() * 2 * Math.PI;
        const speedFactor = dfunctions.bodyWidth() / 800;  // Speed depends on screen width
        var b = {
            id: uuid.v4(),
            variant: dvariants.bubble_variants[variant_index],
            opacity: 0,
            size: random(_min_size.value, _max_size.value),
            movement: drandom.randomFloat(0.2, 1),
            shape: props.shapes[random(0, props.shapes.length - 1)]          
        };
        var half = b.size / 2;
        if(props.pattern == pattern_bounce)
            b.movement =  drandom.randomFloat(0.2, 2) * speedFactor;
        if(props.default_center){
            b.y = dfunctions.bodyHeight() / 2 - half;
            b.x = dfunctions.bodyWidth() / 2 - half;
        }
        else {
            // helt random
            b.y = random(0 + half + 10, dfunctions.bodyHeight()- half - 10);
            b.x = random(0 + half + 10, dfunctions.bodyWidth()- half - 10);
        }
        b.vx = Math.cos(angle) * b.movement; // velocity in x direction
        b.vy = Math.sin(angle) * b.movement; // velocity in y direction
        if (b.shape == 'polygon')
            b.points = getRandomPolygonPoints(random(3, 7));
        _bubbles.value.push(b);
    }
}

function updateBubblePosition(bubble, forced_point) {
    var size = parseInt(bubble.size);
    var dom_bubble = document.getElementById(bubble.id);
    if(props.pattern == pattern_bounce)
    {
        bubble.x += bubble.vx;
        bubble.y += bubble.vy;

        // Check if it hits the horizontal walls (left/right)
        if (bubble.x <= 0 || bubble.x + bubble.size >=  dfunctions.bodyWidth()) {
            bubble.vx = -bubble.vx; // reverse horizontal direction
        }

        // Check if it hits the vertical walls (top/bottom)
        if (bubble.y <= 0 || bubble.y + bubble.size >= dfunctions.bodyHeight()) {
            bubble.vy = -bubble.vy; // reverse vertical direction
        }

        // Apply the updated positions to the DOM element
        dom_bubble.style.transform = `translate(${bubble.x}px, ${bubble.y}px)`;
    }
    else if(props.pattern == pattern_random
        || !props.pattern
    ){
        const movementFactor = parseFloat(bubble.movement) || 0.05; // Default if not set
        var point = forced_point ?? randomPoint(size, movementFactor);
        let duration = random(5, 10); // Random duration between 5-10 seconds
        dom_bubble.style.transition = `transform ${duration}s ease-in-out`;
        dom_bubble.style.transform = `translate(${point.x}, ${point.y})`;
        setTimeout(function () {
            updateBubblePosition(bubble)
        }, duration * 1000);
    }
    
}

function randomPoint(size, factor) {
    var half = size / 2;
    var x_direction = random(0, 1) == 1 ? 1 : -1;
    var y_direction = random(0, 1) == 1 ? 1 : -1;
    var x = random(0 + half, dfunctions.bodyWidth() - half) * x_direction;
    var y = random(0 + half, dfunctions.bodyHeight() - half) * y_direction;
    return { x: (x * factor) + 'px', y: (y * factor) + 'px' };
}

function animate(){
    _bubbles.value.forEach(bubble => {
        updateBubblePosition(bubble);
    });
    if(props.pattern == pattern_bounce)
        requestAnimationFrame(animate);
}

function opacity(){
    _bubbles.value.forEach(bubble => {
        bubble.opacity = drandom.randomFloat(0.1, 0.5);
    });
}

/// mounted
onMounted(() => {
    GenerateBubbles();
    setTimeout(animate, 500);
    setTimeout(opacity, 500);
})


</script>